/**
 * File Storage Utilities
 * Helper functions for handling file URLs and uploads
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get the full URL for a file
 * Handles base64 data URLs, S3 presigned URLs, and local file storage URLs
 * 
 * @param {string} fileUrl - The file URL or base64 data
 * @returns {string} The full URL to access the file
 */
export const getFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  
  // If it's already a data URL (base64), return as-is
  if (fileUrl.startsWith('data:')) {
    return fileUrl;
  }
  
  // If it's an S3 presigned URL or any full URL, return as-is
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  
  // If it's a relative file storage URL, prepend the API URL
  if (fileUrl.startsWith('/api/files/')) {
    return `${API_URL}${fileUrl}`;
  }
  
  // Fallback: assume it's a relative path
  return `${API_URL}${fileUrl}`;
};

/**
 * Check if a URL is a file storage URL (not base64)
 * 
 * @param {string} url - The URL to check
 * @returns {boolean} True if it's a file storage URL
 */
export const isFileStorageUrl = (url) => {
  if (!url) return false;
  return url.startsWith('/api/files/') || url.includes('/api/files/');
};

/**
 * Check if a URL is an S3 presigned URL
 * 
 * @param {string} url - The URL to check
 * @returns {boolean} True if it's an S3 presigned URL
 */
export const isS3PresignedUrl = (url) => {
  if (!url) return false;
  return url.includes('s3.') && url.includes('amazonaws.com') && url.includes('X-Amz-Signature');
};

/**
 * Upload a file using the file storage API
 * 
 * @param {File} file - The file to upload
 * @param {string} category - The category (e.g., 'vehicles', 'documents', 'receipts')
 * @param {string} token - The auth token
 * @returns {Promise<{file_key: string, file_url: string}>}
 */
export const uploadFile = async (file, category, token) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  
  const response = await fetch(`${API_URL}/api/files/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to upload file');
  }
  
  return response.json();
};

/**
 * Upload a base64 file using the file storage API
 * 
 * @param {string} base64Data - The base64 data URL
 * @param {string} category - The category (e.g., 'vehicles', 'documents')
 * @param {string} filename - The filename
 * @param {string} token - The auth token
 * @returns {Promise<{file_key: string, file_url: string}>}
 */
export const uploadBase64File = async (base64Data, category, filename, token) => {
  const response = await fetch(`${API_URL}/api/files/upload-base64`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      base64_data: base64Data,
      category: category,
      filename: filename
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to upload file');
  }
  
  return response.json();
};

/**
 * Delete a file from storage
 * 
 * @param {string} fileKey - The file key to delete
 * @param {string} token - The auth token
 * @returns {Promise<void>}
 */
export const deleteFile = async (fileKey, token) => {
  const response = await fetch(`${API_URL}/api/files/${fileKey}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok && response.status !== 404) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete file');
  }
};

export default {
  getFileUrl,
  isFileStorageUrl,
  isS3PresignedUrl,
  uploadFile,
  uploadBase64File,
  deleteFile
};
